/**
 * EmployerBookings — self-serve booking history for logged-in employers.
 *
 * Backend contract:
 *   GET  /api/employer/bookings                       → list bookings (with can_cancel/hours_until)
 *   POST /api/employer/bookings/{bid}/cancel {reason} → cancels if >= 48h ahead
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Calendar, Clock, X, AlertCircle, CheckCircle2, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { useEmployer } from "@/context/EmployerContext";
import PublicLayout from "@/components/PublicLayout";

export default function EmployerBookings() {
  const { t } = useTranslation();
  const { employer } = useEmployer();
  const qc = useQueryClient();
  const [cancellingId, setCancellingId] = useState(null);
  const [reason, setReason] = useState("");

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ["employer-bookings"],
    queryFn: async () => (await api.get("/employer/bookings")).data,
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ bid, reason }) => {
      return await api.post(`/employer/bookings/${bid}/cancel`, { reason });
    },
    onSuccess: () => {
      toast.success("Booking cancelled successfully");
      qc.invalidateQueries({ queryKey: ["employer-bookings"] });
      setCancellingId(null);
      setReason("");
    },
    onError: (e) => {
      toast.error(e.response?.data?.detail || "Cancel failed");
    },
  });

  const submitCancel = (bid) => {
    if (!confirm("Are you sure you want to cancel this booking? This action is irreversible.")) return;
    cancelMutation.mutate({ bid, reason });
  };

  const statusBadge = (status) => {
    const s = (status || "").toLowerCase();
    const map = {
      confirmed: "bg-emerald-100 text-emerald-800 border-emerald-300",
      pending: "bg-amber-100 text-amber-800 border-amber-300",
      cancelled: "bg-red-100 text-red-700 border-red-300",
      completed: "bg-zinc-100 text-zinc-700 border-zinc-300",
    };
    return (
      <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${map[s] || map.pending}`}>
        {status || "pending"}
      </span>
    );
  };

  if (!employer) {
    return (
      <PublicLayout>
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="text-zinc-600">Please <Link to="/employer/login" className="text-[#b8860b] underline">sign in</Link> to view your bookings.</div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link to="/catalog" className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-950 mb-4" data-testid="back-to-catalog">
          <ChevronLeft size={14} /> Back to catalog
        </Link>
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl text-zinc-950 tracking-tight">My Bookings</h1>
            <div className="text-[11.5px] uppercase tracking-[0.18em] text-zinc-500 mt-1 font-medium">
              {employer.name || employer.email} — Interview appointments
            </div>
          </div>
        </div>

        {isLoading && <div className="p-10 text-center text-zinc-500 text-sm">Loading bookings…</div>}
        {!isLoading && bookings.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-zinc-200 bg-white p-12 text-center" data-testid="no-bookings">
            <Calendar size={36} className="mx-auto text-zinc-300" />
            <div className="mt-3 text-sm font-medium text-zinc-700">No bookings yet</div>
            <div className="text-[11.5px] text-zinc-500 mt-1">Browse candidates and reserve interview time slots.</div>
            <Link to="/catalog" className="inline-flex mt-4 h-9 px-4 rounded-full bg-[#b8860b] text-white text-[12.5px] font-semibold items-center">
              Browse candidates
            </Link>
          </div>
        )}

        <div className="space-y-3">
          {bookings.map((b) => {
            const items = b.items || [];
            const isCancelled = (b.status || "").toLowerCase() === "cancelled";
            const isCompleted = (b.status || "").toLowerCase() === "completed";
            return (
              <div key={b.id} className={`bg-white rounded-2xl border ${isCancelled ? "border-red-200 bg-red-50/30" : "border-zinc-200"} p-5`} data-testid={`booking-${b.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-mono text-[11px] text-zinc-500">#{b.id.slice(0, 8)}</div>
                      {statusBadge(b.status)}
                      {b.hours_until !== null && b.hours_until !== undefined && !isCancelled && !isCompleted && (
                        <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold ${
                          b.hours_until < 48 ? "text-red-700" : b.hours_until < 96 ? "text-amber-700" : "text-zinc-600"
                        }`}>
                          <Clock size={11} /> in {b.hours_until}h
                        </span>
                      )}
                    </div>
                    <div className="mt-3 space-y-1.5">
                      {items.map((it, i) => (
                        <div key={`${b.id}-item-${it.candidate_id || i}-${it.date || i}`} className="text-[13px] text-zinc-800 flex items-center gap-2">
                          <Calendar size={12} className="text-zinc-400" />
                          <span className="font-medium">{it.candidate_name || it.candidate_id}</span>
                          <span className="text-zinc-500">·</span>
                          <span>{it.date} @ {it.time}</span>
                        </div>
                      ))}
                    </div>
                    {b.notes && (
                      <div className="mt-3 text-[11.5px] text-zinc-600 italic bg-zinc-50 p-2 rounded">
                        {b.notes}
                      </div>
                    )}
                    {isCancelled && b.cancel_reason && (
                      <div className="mt-3 text-[11.5px] text-red-700 bg-red-50 border border-red-200 p-2 rounded flex items-start gap-2">
                        <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                        <div>
                          <b>Cancelled</b> {b.cancelled_by ? `by ${b.cancelled_by}` : ""}
                          {b.cancel_reason && <>: {b.cancel_reason}</>}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {b.can_cancel && cancellingId !== b.id && (
                      <button
                        type="button"
                        onClick={() => setCancellingId(b.id)}
                        data-testid={`cancel-btn-${b.id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-red-300 text-red-700 text-[11.5px] font-semibold hover:bg-red-50"
                      >
                        <X size={12} /> Cancel booking
                      </button>
                    )}
                    {!b.can_cancel && !isCancelled && !isCompleted && b.hours_until !== null && b.hours_until < 48 && (
                      <div className="text-[10.5px] text-zinc-500 italic max-w-[150px] text-right">
                        Cancellation window closed ({b.hours_until}h remaining — 48h minimum required)
                      </div>
                    )}
                    {(isCompleted) && <CheckCircle2 size={16} className="text-emerald-600" />}
                  </div>
                </div>
                {cancellingId === b.id && (
                  <div className="mt-4 p-3 border-t border-zinc-200 space-y-2" data-testid={`cancel-panel-${b.id}`}>
                    <div className="text-[11px] uppercase tracking-widest text-red-700 font-semibold">Cancel this booking</div>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Reason (optional, helps us improve)"
                      rows={2}
                      className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm"
                      data-testid={`cancel-reason-${b.id}`}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => submitCancel(b.id)}
                        disabled={cancelMutation.isPending}
                        data-testid={`cancel-confirm-${b.id}`}
                        className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-red-600 text-white text-[12px] font-semibold hover:bg-red-700 disabled:opacity-50"
                      >
                        {cancelMutation.isPending ? "Cancelling…" : "Confirm cancel"}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setCancellingId(null); setReason(""); }}
                        className="inline-flex items-center gap-1 h-9 px-4 rounded-full border border-zinc-300 text-zinc-700 text-[12px] font-semibold hover:bg-zinc-50"
                      >
                        Keep booking
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </PublicLayout>
  );
}
